import { Toaster, Wordmark, cn } from '@westhetics/ui'
import type { FormEvent, ReactNode } from 'react'
import { column, gutter, noteText } from './shell'

/**
 * A moldura das telas sem sessão (entrar, cadastro pelo link): pampas, o wordmark no alto, a coluna
 * de leitura, o rodapé. Com `onSubmit` a coluna é um formulário; sem ele, serve ao estado que só
 * informa e oferece a saída.
 */
export function PublicScreen({
  eyebrow,
  title,
  onSubmit,
  footer = 'Sessão de 30 dias neste aparelho · © 2026 Westhetics',
  header,
  tone = 'pampas',
  wide = false,
  children,
}: {
  eyebrow: string
  title: ReactNode
  onSubmit?: (event: FormEvent) => void
  /** A linha miúda do fim. */
  footer?: ReactNode
  /** Algo à direita do wordmark, como o logo da clínica. */
  header?: ReactNode
  /** `ink`: o fundo escuro da tela final ("Assinado."), com o wordmark em pampas. */
  tone?: 'pampas' | 'ink'
  /** A coluna inteira de leitura (640px) em vez dos 420px das telas de formulário. */
  wide?: boolean
  children: ReactNode
}) {
  const dark = tone === 'ink'
  const content = (
    <>
      <div className="flex items-center justify-between gap-4">
        <Wordmark className={cn('h-[13px] w-auto', dark ? 'text-pampas' : 'text-ink')} />
        {header}
      </div>
      <div className={cn('my-auto flex flex-col py-10', wide ? 'w-full' : 'max-w-[420px]')}>
        <div className={cn('eyebrow', dark && 'text-pampas/70')}>{eyebrow}</div>
        <h1
          className={cn(
            'font-display mt-3 text-[28px] leading-[1.15] font-light text-pretty',
            dark ? 'text-pampas' : 'text-ink',
          )}
        >
          {title}
        </h1>
        {children}
      </div>
      <p className={cn(noteText, dark && 'text-pampas/60')}>{footer}</p>
    </>
  )
  const className = cn(
    column,
    gutter,
    'flex flex-1 flex-col pt-[max(1.75rem,env(safe-area-inset-top))] pb-8',
  )
  return (
    <>
      <Toaster />
      <div
        className={cn(
          'flex min-h-dvh flex-col',
          dark ? 'bg-ink text-pampas' : 'bg-pampas text-ink',
        )}
      >
        {onSubmit ? (
          <form onSubmit={onSubmit} noValidate className={className}>
            {content}
          </form>
        ) : (
          <div className={className}>{content}</div>
        )}
      </div>
    </>
  )
}

/** O campo das telas sem sessão: só o fio de baixo, como no acesso da clínica. */
export const publicField =
  'block w-full border-0 border-b border-[rgba(26,26,26,.32)] bg-transparent px-0 py-3 font-body text-[16px] desk:text-[14px] leading-none font-light text-ink outline-none placeholder:text-label-soft focus:border-ink aria-invalid:border-plum'

/**
 * O campo de `@westhetics/ui` (`Input`, `Select`) nas telas sem sessão com formulário longo (cadastro
 * pelo link): 48px e 16px abaixo de 900px, para o alvo de toque e para o iOS não ampliar a tela.
 */
export const touchField = 'max-desk:min-h-touch max-desk:text-[16px] scroll-mt-24 scroll-mb-40'

/** A ação em texto ("Usar outro e-mail"), com o alvo de 48px abaixo de 900px. */
export const publicTextAction =
  "font-body text-label hover:text-ink border-hairline-heavy relative cursor-pointer self-start border-b pb-[2px] text-[12px] leading-none font-light max-desk:after:absolute max-desk:after:inset-x-0 max-desk:after:top-1/2 max-desk:after:h-touch max-desk:after:-translate-y-1/2 max-desk:after:content-['']"

/** A linha abaixo do campo: a dica, ou o erro no lugar dela (nasce ao sair do campo). */
export function FieldNote({
  id,
  error,
  hint,
}: {
  id: string
  error?: string | null
  hint?: ReactNode
}) {
  if (!error && !hint) return null
  return (
    <p
      id={id}
      role={error ? 'alert' : undefined}
      className={cn(
        'font-body mt-2 text-[12px] leading-[1.5] font-light',
        error ? 'text-plum' : 'text-label',
      )}
    >
      {error ?? hint}
    </p>
  )
}
